import { useEffect, useState } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Loader2, Mail, ArrowRightCircle, ArrowDownCircle, ShieldAlert, Info } from "lucide-react";

const DIR = {
  out: { label: "AI → Customer", cls: "text-signal-500 border-signal-500/40", Icon: ArrowRightCircle },
  in: { label: "Customer → AI", cls: "text-emerald-400 border-emerald-400/40", Icon: ArrowDownCircle },
  internal: { label: "System → Rok", cls: "text-violet-400 border-violet-400/40", Icon: ShieldAlert },
};

export default function ActivationFlowPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/activation-workflow/preview"); setData(data); }
      catch { toast.error("Could not load the flow preview"); }
      setLoading(false);
    })();
  }, []);

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="activation-flow-page">
        <div>
          <h1 className="text-2xl font-display font-bold tracking-tight">Activated-Lead Flow — Preview</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Exactly what the AI would send at each stage of an activated lead. <b>Dry-run — nothing is sent.</b>
          </p>
        </div>

        <div className="rounded-sm border border-signal-500/30 bg-signal-500/5 p-4 text-sm flex gap-3" data-testid="activation-flow-policy">
          <Info className="w-4 h-4 text-signal-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <div>From the first reply after activation, every email <b>CCs {data?.cc_policy || "rok.kobal@europacific.com"}</b> and ends with the RFQ CTA to <b>{data?.quote_portal}</b>.</div>
            <div>Each CC'd email carries a red <b>“I'll take it from here — stop the AI”</b> button. Clicking it pauses the AI for that lead.</div>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : !data ? (
          <div className="py-16 text-center text-sm text-muted-foreground">No preview available.</div>
        ) : (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground">
              Sample lead: <b className="text-foreground">{data.lead.full_name}</b>, {data.lead.title} @ {data.lead.company} ({data.lead.country})
            </div>
            {data.emails.map((e, i) => {
              const d = DIR[e.direction] || DIR.out;
              return (
                <div key={e.id} className="border border-border rounded-sm overflow-hidden" data-testid={`flow-email-${i}`}>
                  <div className="px-4 py-2.5 bg-muted/40 flex items-center gap-2 flex-wrap">
                    <span className="text-[0.7rem] uppercase tracking-wider text-muted-foreground font-semibold">{e.stage}</span>
                    <span className={`ml-auto text-[0.6rem] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${d.cls}`}>
                      <d.Icon className="w-3 h-3" />{d.label}
                    </span>
                    {e.has_takeover && <span className="text-[0.6rem] px-2 py-0.5 rounded-full border border-red-500/40 text-red-400" data-testid={`flow-takeover-badge-${i}`}>Takeover button</span>}
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="grid grid-cols-[52px_1fr] gap-y-1 text-xs">
                      <span className="text-muted-foreground">To</span><span className="truncate">{e.to}</span>
                      {e.cc && (<><span className="text-signal-500">Cc</span><span className="text-signal-500 font-medium" data-testid={`flow-cc-${i}`}>{e.cc}</span></>)}
                      <span className="text-muted-foreground">Subject</span><span className="font-medium">{e.subject}</span>
                    </div>
                    <div className="border-t border-border pt-3 text-sm leading-relaxed [&_a]:text-signal-500" dangerouslySetInnerHTML={{ __html: e.body_html }} />
                    {e.note && <div className="text-[0.7rem] text-muted-foreground italic border-t border-border pt-2">{e.note}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
